import { useCallback, useMemo, useState } from 'react'
import { ActionIcon, Box, Center, Group, HoverCard, Loader, Stack, Text, Tooltip } from '@mantine/core'
import { IconBookmark, IconBookmarkFilled, IconExternalLink, IconNotes } from '@/components/icons'
import { TicketListTagsRow } from '@/components/tickets/TicketListTagsRow'
import { useBookmarks } from '@/lib/bookmarks-context'
import { ticketPathSegmentFromListItem } from '@/lib/ticket-display'
import {
  formatSubjectPreviewClientMessages,
  formatSubjectPreviewTooltip,
  getTicketSubjectPreviewMessages,
} from '@/lib/ticket-subject-preview'
import { getCachedTicketPreviewMode } from '@/lib/workspace-ticket-preview-mode'
import { useWorkspaceTabs } from '@/lib/workspace-tabs-context'
import type { ProjectTagRow, TicketListItem } from '@/types/api'

type Phase = 'idle' | 'loading' | 'done' | 'error'

/** Отступ слева под иконку закладки + gap (как у строки темы). */
const SUBJECT_TAGS_INDENT_PX = 32

export function TicketListSubjectCell({
  ticket,
  subject,
  tagIds,
  projectTags,
}: {
  ticket: TicketListItem
  subject: string
  tagIds?: string[] | null
  projectTags?: ProjectTagRow[] | null
}) {
  const ticketId = ticket.id
  const display = subject?.trim() || '—'
  const tagsRow = useMemo(() => {
    const ids = tagIds?.filter((x): x is string => typeof x === 'string' && x.length > 0) ?? []
    const tags = projectTags ?? []
    if (!ids.length || !tags.length) return null
    const byId = new Map(tags.map((t) => [t.id, t]))
    const hasAny = ids.some((id) => byId.has(id))
    if (!hasAny) return null
    return <TicketListTagsRow tagIds={ids} projectTags={tags} />
  }, [tagIds, projectTags])
  const { isBookmarked, toggleBookmark } = useBookmarks()
  const { openNewTab } = useWorkspaceTabs()
  const bookmarked = isBookmarked(ticketId)
  const [bmBusy, setBmBusy] = useState(false)

  const [phase, setPhase] = useState<Phase>('idle')
  const [body, setBody] = useState('')

  const onBookmarkClick = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (bmBusy) return
      setBmBusy(true)
      try {
        await toggleBookmark(ticketId)
      } finally {
        setBmBusy(false)
      }
    },
    [ticketId, toggleBookmark],
  )

  const handleOpen = useCallback(() => {
    if (phase === 'loading') return
    if (phase === 'done') return

    setPhase('loading')
    const mode = getCachedTicketPreviewMode()
    void getTicketSubjectPreviewMessages(ticketId)
      .then((msgs) => {
        const t =
          mode === 'messages'
            ? formatSubjectPreviewClientMessages(msgs)
            : formatSubjectPreviewTooltip(msgs)
        setBody(t ?? '')
        setPhase('done')
      })
      .catch(() => {
        setPhase('error')
      })
  }, [ticketId, phase])

  const handleClose = useCallback(() => {
    if (phase === 'loading') setPhase('idle')
  }, [phase])

  const onOpenInNewWorkspaceTab = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      e.preventDefault()
      const ref = ticketPathSegmentFromListItem(ticket)
      openNewTab(`/tickets/${encodeURIComponent(ref)}`, { activate: false })
    },
    [ticket, openNewTab],
  )

  return (
    <Stack gap={2} style={{ width: '100%', minWidth: 0 }}>
      <Group gap={4} wrap="nowrap" align="flex-start" justify="flex-start" style={{ width: '100%', minWidth: 0 }}>
        <ActionIcon
          variant="subtle"
          size="sm"
          radius="md"
          loading={bmBusy}
          aria-label={bookmarked ? 'Удалить из закладок' : 'Добавить в закладки'}
          title={bookmarked ? 'Удалить из закладок' : 'Добавить в закладки'}
          color={bookmarked ? 'yellow' : 'gray'}
          onClick={onBookmarkClick}
          style={{ flexShrink: 0, marginTop: 2 }}
        >
          {bookmarked ? <IconBookmarkFilled size={16} stroke={1.5} /> : <IconBookmark size={16} stroke={1.5} />}
        </ActionIcon>
        <Text component="span" size="sm" lh={1.35} truncate style={{ flex: 1, minWidth: 0 }}>
          {display}
        </Text>
        <Tooltip label="Открыть в новой вкладке" withArrow position="top">
          <ActionIcon
            variant="subtle"
            size="sm"
            radius="md"
            aria-label="Открыть в новой вкладке"
            style={{ flexShrink: 0, marginTop: 2 }}
            onClick={onOpenInNewWorkspaceTab}
          >
            <IconExternalLink size={16} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
        <HoverCard
          width={480}
          shadow="md"
          position="bottom-end"
          openDelay={400}
          closeDelay={120}
          withinPortal
          onOpen={handleOpen}
          onClose={handleClose}
          styles={{
            dropdown: {
              maxHeight: 'none',
              overflow: 'visible',
            },
          }}
        >
          <HoverCard.Target>
            <ActionIcon
              variant="subtle"
              size="sm"
              radius="md"
              aria-label="Превью: саммари или первое и последнее сообщение"
              title="Превью переписки"
              style={{ flexShrink: 0, marginTop: 2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <IconNotes size={16} stroke={1.5} />
            </ActionIcon>
          </HoverCard.Target>
          <HoverCard.Dropdown p="sm">
            {phase === 'idle' || phase === 'loading' ? (
              <Center py="md">
                <Loader size="sm" type="dots" />
              </Center>
            ) : null}
            {phase === 'error' ? (
              <Text size="xs" c="red">
                Не удалось загрузить сообщения
              </Text>
            ) : null}
            {phase === 'done' ? (
              body ? (
                <Text size="xs" lh={1.55} style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {body}
                </Text>
              ) : (
                <Text size="xs" c="dimmed">
                  Нет сообщений для превью
                </Text>
              )
            ) : null}
          </HoverCard.Dropdown>
        </HoverCard>
      </Group>
      {tagsRow ? (
        <Box style={{ paddingLeft: SUBJECT_TAGS_INDENT_PX, minWidth: 0 }}>{tagsRow}</Box>
      ) : null}
    </Stack>
  )
}
